import React, { useEffect, useState } from "react"
import HTMLReactParser from "html-react-parser"
import "./Autocomplete.css"
import { mapConfig as conf } from "./config"
import useScript from "..//hooks/useScript"
import { TextField } from "../reusable/Form/Fields"
import { Flex } from "../reusable/common"

const Autocomplete = ({
  map,
  setLocationLabel,
  setLocationHooks,
  register,
  defaultValue = "",
}) => {
  const [keyword, setKeyword] = useState(defaultValue ? defaultValue : "")
  const [results, setResults] = useState([])
  const [autocompleteService, setAutocompleteService] = useState(null)
  const woosmapLoaded = useScript(conf.woosmapMultiSearchUrl)

  useEffect(() => {
    if (woosmapLoaded) {
      const autocompleteService = window.woosmap.multisearch({
        apiOrder: ["localities", "store", "address", "places"],
        debounceTime: 100,
        store: {
          key: conf.woosmapLoadOptions.publicKey,
          fallbackBreakpoint: 0.5,
        },
        localities: {
          key: conf.woosmapLoadOptions.publicKey,
          fallbackBreakpoint: 0.4,
          params: {
            components: {
              country: ["GB"],
              language: "en",
              types: ["locality", "country", "postal_code"],
            },
          },
        },
        address: {
          key: conf.woosmapLoadOptions.publicKey,
          fallbackBreakpoint: 0.5,
          minInputLength: 5,
          params: {
            components: { country: ["GBR"] },
            language: "en",
          },
        },
        places: {
          key: conf.woosmapLoadOptions.publicKey,
          fallbackBreakpoint: 1,
          minInputLength: 10,
        },
      })
      setAutocompleteService(autocompleteService)
    }
  }, [woosmapLoaded])

  const getSearchDetails = async suggestion => {
    const details = await autocompleteService.detailsMulti({
      id: suggestion.id,
      api: suggestion.api,
    })

    return details
  }

  const handleKeyInput = async ({ keyCode }) => {
    // hit enter to select first result
    if (keyCode === 13 && results.length > 0) {
      // get first result to use for searchNearbyStores
      const selection = results[0]
      const selectionDetails = await getSearchDetails(selection)

      if (selectionDetails) {
        searchNearbyStores(
          selection.description,
          selectionDetails.geometry.location
        )
      }
    }

    // hit escape to clear field
    if (keyCode === 27) {
      updateText("")
    }
  }

  const updateText = text => {
    setKeyword(text)
    setLocationHooks(null)
    if (!text) {
      setResults([])
    }
  }
  const updateKeyword = text => {
    updateText(text)
    onSearch(text)
  }

  const searchNearbyStores = (text, location) => {
    updateText(text)
    setLocationLabel(text)
    setLocationHooks(location)
    setResults([])
  }

  const onSearch = async text => {
    if (text !== "") {
      autocompleteService.autocompleteMulti(text).then(
        response => {
          setResults(response)
        },
        (errorCode, errorText) => {
          console.error(`error ${errorCode} : ${errorText}`)
        }
      )
    } else setResults([])
  }

  const renderResults = results.map(
    ({ description, highlight, item }, index) => {
      return (
        <PredictionPreview
          key={item.public_id}
          results={results}
          getSearchDetails={getSearchDetails}
          searchNearbyStores={searchNearbyStores}
          index={index}
          description={description}
          highlight={highlight}
          location={item.location}
        />
      )
    }
  )
  return (
    <div className="autocomplete max-md:!basis-full">
      <Flex
        style={{
          position: "relative",
          width: "100%",
        }}
      >
        <button
          type="button"
          onClick={() => updateText("")}
          className={`autocomplete__cancelBtn ${
            keyword.length > 0 ? "active" : "inactive"
          }`}
        >
          <img src="https://images.woosmap.com/close.svg" alt="reset" />
        </button>
        <TextField
          id="location"
          label="Enter a postcode, location or pub name"
          value={keyword}
          register={register}
          registerValue={{
            onChange: e => {
              return updateKeyword(e.target.value)
            },
          }}
          onKeyDown={handleKeyInput}
        />
      </Flex>

      {results.length > 0 ? (
        <div className="predictionsResults">{renderResults}</div>
      ) : null}
    </div>
  )
}

const PredictionPreview = ({
  description,
  highlight,
  index,
  results,
  getSearchDetails,
  searchNearbyStores,
  location,
}) => {
  const handleClick = async () => {
    // get first result to use for searchNearbyStores
    const selection = results[index]
    const selectionDetails = await getSearchDetails(selection)

    if (selectionDetails) {
      searchNearbyStores(
        selection.description,
        selectionDetails.geometry.location
      )
    }
  }

  return (
    <div
      role="presentation"
      className={`predictionPreview ${index === 0 ? "selected" : ""}`}
      onClick={() => handleClick()}
      onKeyDown={() => handleClick()}
    >
      <div className="predictionPreview__locality">
        <p className="predictionPreview__localityDesc">
          {HTMLReactParser(highlight || description)}
        </p>
      </div>
    </div>
  )
}

export default Autocomplete
